import { useState } from "react";
import { http, track, errText, inr } from "../../lib/api";
import { Reveal } from "../Reveal";
import { DatePicker } from "../DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

function Disclosures({ list }) {
  return (
    <div className="rounded-2xl bg-[#fff0e6] p-3 text-xs text-[#191c1f]/80 space-y-1">
      {(list || []).map((d, i) => <p key={i}>• {d}</p>)}
    </div>
  );
}

function Checkout({ plan, settings, onClose }) {
  const [d, setD] = useState({ name: "", mobile: "", email: "", lead_reference: "" });
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState("form"); // form | paying | done | booking | booked
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [booking, setBooking] = useState({ preferred_day: "", window: "Morning" });
  const set = (k, v) => setD((o) => ({ ...o, [k]: v }));
  const field = "w-full min-h-[44px] rounded-2xl border-2 border-[#e1e2e5] px-4 py-2.5 font-medium focus:border-[#004040] outline-none";

  const startPay = async () => {
    if (d.name.trim().length < 2 || !/^[6-9]\d{9}$/.test(d.mobile.replace(/\D/g, "").slice(-10)) || !d.email.includes("@")) { setErr("Fill name, valid mobile and email."); return; }
    if (!consent) { setErr("Please accept the Consulting Plans Terms."); return; }
    setErr(""); setStep("paying");
    try {
      track("plan_selected", { plan: plan.id });
      const { data } = await http.post("/orders", { plan: plan.id, name: d.name, email: d.email, mobile: "+91" + d.mobile.replace(/\D/g, "").slice(-10), lead_reference: d.lead_reference, consent: true });
      setOrder(data);
      if (data.provider === "razorpay" && window.Razorpay) {
        const rz = new window.Razorpay({
          key: data.razorpay_key_id, amount: data.amount * 100, currency: "INR",
          name: "Bhartiya Loan Capital", description: `${data.plan_label} plan`, order_id: data.razorpay_order_id,
          prefill: { name: d.name, email: d.email, contact: d.mobile },
          handler: async (r) => { await verify({ order_code: data.order_code, razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature }); },
        });
        rz.on("payment.failed", () => { setErr("Payment failed. Please retry."); setStep("form"); });
        rz.open();
      } else {
        // demo mode — no real keys yet
        await verify({ order_code: data.order_code, demo: true });
      }
    } catch (e) { setErr(errText(e?.response?.data?.detail)); setStep("form"); }
  };

  const verify = async (payload) => {
    try {
      await http.post("/payments/verify", payload);
      track("payment_success", { plan: plan.id });
      setStep("done");
    } catch (e) { setErr(errText(e?.response?.data?.detail)); setStep("form"); }
  };

  const book = async () => {
    try {
      await http.post("/bookings", { order_code: order.order_code, mobile: "+91" + d.mobile.replace(/\D/g, "").slice(-10), preferred_day: booking.preferred_day, window: booking.window });
      setStep("booked");
    } catch (e) { setErr(errText(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="checkout">
      {step === "form" && (
        <>
          <div className="rounded-2xl bg-[#f4f5f6] p-4 mb-3">
            <div className="font-extrabold text-lg">{plan.label} — Rs {plan.price}</div>
            <div className="text-sm text-[#6b6f76]">{plan.minutes} minutes consulting · Rs {plan.per_minute}/min</div>
          </div>
          <div className="grid gap-2">
            <input className={field} placeholder="Full name" value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="checkout-name" aria-label="Name" />
            <div className="flex items-center rounded-2xl border-2 border-[#e1e2e5] overflow-hidden focus-within:border-[#004040]">
              <span className="px-3 text-[#6b6f76] font-bold">+91</span>
              <input className="flex-1 min-h-[44px] px-2 outline-none" inputMode="numeric" maxLength={10} placeholder="Mobile" value={d.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="checkout-mobile" aria-label="Mobile" />
            </div>
            <input className={field} placeholder="Email" value={d.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" aria-label="Email" />
            <input className={field} placeholder="Loan reference (optional), e.g. BLC-2026-000001" value={d.lead_reference} onChange={(e) => set("lead_reference", e.target.value)} data-testid="checkout-ref" aria-label="Loan reference" />
          </div>
          <div className="mt-3"><Disclosures list={settings.plan_disclosures} /></div>
          <label className="flex items-start gap-3 text-sm mt-3 cursor-pointer">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#f06400]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="checkout-consent" />
            <span>I accept the Consulting Plans Terms and the disclosures above.</span>
          </label>
          {err && <p className="text-sm text-[#8a2412] font-bold mt-2" data-testid="checkout-error">{err}</p>}
          <button className="w-full mt-4 min-h-[48px] rounded-full bg-[#f06400] text-[#191c1f] font-extrabold hover:bg-[#d85800]" onClick={startPay} data-testid="checkout-pay">
            Pay Rs {plan.price}
          </button>
        </>
      )}
      {step === "paying" && <p className="py-10 text-center font-bold" data-testid="checkout-paying">Processing payment…</p>}
      {step === "done" && (
        <div className="text-center py-2" data-testid="checkout-done">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#e2f0ef] grid place-items-center mb-2"><span className="text-[#004040] text-2xl font-extrabold">✓</span></div>
          <h3 className="text-xl font-extrabold">Payment successful</h3>
          <p className="mt-1 text-sm text-[#6b6f76]">Order code</p>
          <p className="text-xl font-extrabold text-[#004040]" data-testid="checkout-order-code">{order.order_code}</p>
          <p className="mt-1 text-sm text-[#6b6f76]">A receipt is on its way to your email. Now pick a time.</p>
          <div className="text-left mt-4 grid gap-2">
            <label className="text-sm font-bold">Preferred day</label>
            <DatePicker value={booking.preferred_day} onChange={(v) => setBooking((b) => ({ ...b, preferred_day: v }))} testId="booking-day" />
            <label className="text-sm font-bold">Window</label>
            <div className="inline-flex rounded-full bg-[#f4f5f6] p-1">
              {["Morning", "Afternoon", "Evening"].map((w) => (
                <button key={w} onClick={() => setBooking((b) => ({ ...b, window: w }))} data-testid={`booking-window-${w.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold ${booking.window === w ? "bg-[#191c1f] text-white" : ""}`}>{w}</button>
              ))}
            </div>
            <button className="min-h-[48px] rounded-full bg-[#191c1f] text-white font-bold mt-2" onClick={book} data-testid="booking-submit" disabled={!booking.preferred_day}>Request this time</button>
            <a href="/my-plan" className="text-center text-sm font-bold underline mt-1" data-testid="checkout-myplan-link">Go to My plan</a>
          </div>
        </div>
      )}
      {step === "booked" && (
        <div className="text-center py-6" data-testid="checkout-booked">
          <h3 className="text-xl font-extrabold">Booking requested</h3>
          <p className="mt-2 text-sm text-[#6b6f76]">Our consultant will confirm the exact time by call or WhatsApp.</p>
          <a href="/my-plan" className="inline-block mt-4 min-h-[44px] px-6 rounded-full bg-[#191c1f] text-white font-bold leading-[44px]">Go to My plan</a>
        </div>
      )}
    </div>
  );
}

export function Plans({ settings }) {
  const [active, setActive] = useState(null);
  const plans = settings?.plans || [];
  return (
    <section id="plans" className="bg-[#f4f5f6] py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">Not approved yet? Get guided.</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em] max-w-2xl">Our in-house consultants show you what to fix and how to apply again.</h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 100}>
              <div className={`rounded-[28px] bg-white p-6 h-full flex flex-col border ${p.badge ? "border-[#f06400] shadow-lg" : "border-[#e1e2e5]"}`} data-testid={`plan-card-${p.id}`}>
                {p.badge && <span className="self-start text-xs font-extrabold px-3 py-1 rounded-full bg-[#fff0e6] text-[#b84a00] mb-2">{p.badge}</span>}
                <h3 className="text-2xl font-extrabold">{p.label}</h3>
                <div className="mt-1 flex items-end gap-1">
                  <span className="text-4xl font-extrabold tracking-[-0.03em]">Rs {p.price}</span>
                </div>
                <p className="text-sm text-[#6b6f76] font-bold">{p.minutes} minutes · Rs {p.per_minute}/min</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.includes.map((x) => <li key={x} className="flex gap-2 text-sm"><span className="text-[#004040] font-extrabold shrink-0">✓</span>{x}</li>)}
                </ul>
                <div className="mt-4"><Disclosures list={settings.plan_disclosures} /></div>
                <button className={`mt-4 min-h-[48px] rounded-full font-extrabold ${p.badge ? "bg-[#f06400] text-[#191c1f] hover:bg-[#d85800]" : "bg-[#191c1f] text-white hover:bg-black"}`}
                  data-testid={`consulting-plan-${p.id}-buy`} onClick={() => { setActive(p); track("plan_viewed", { plan: p.id }); }}>
                  Choose {p.label}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#6b6f76]">
          One-time purchase, not auto-debit. Valid {settings?.plan_rules?.validity_days || 30} days. Minutes counted in 1-minute steps and logged by the consultant. Unused minutes expire.
          Already bought a plan? <a href="/my-plan" className="font-bold underline" data-testid="plans-myplan-link">Open My plan</a>.
        </p>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md w-[calc(100%-1.5rem)] sm:w-full rounded-[28px] max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Checkout</DialogTitle></DialogHeader>
          {active && <Checkout plan={active} settings={settings} onClose={() => setActive(null)} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}
